import * as React from 'react';
import { observer } from 'mobx-react';
import { PreviewCycleConfirmationModal } from 'nomst/Components/CycleSettings/previewCycleConfirmationModal';
import { CloseCycleConfirmationModal } from 'nomst/Components/CycleSettings/closeCycleConfirmationModal';
import { OpenCycleConfirmationModal } from 'nomst/Components/CycleSettings/openCycleConfirmationModal';
import { SyncToLabConfirmationModal } from 'nomst/Components/CycleSettings/syncToLabConfirmationModal';
import { ResetCycleConfirmationModal } from 'nomst/Components/CycleSettings/resetCycleConfirmationModal';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { ConfigurationsStore } from 'nomst/Stores/Configuration/configurations.store';
import { workflowSettingsViewStore, cycleRules } from 'utils/MobxDataManager/singletons';
import { Tooltip } from '@payscale/design';
import * as actions from 'nomst/Actions/configurations';
import './cycleActionButtons.scss';

export interface ICycleActionButtonsProps {
    isDisabled: boolean;
    configuration: Configuration;
    configurationsStore: ConfigurationsStore;
    isHrAdmin: boolean;
}

export interface ICycleActionButtonsState {
    isPreviewCycleModalOpen: boolean;
    isResetCycleModalOpen: boolean;
    isOpenCycleModalOpen: boolean;
    isCloseCycleModalOpen: boolean;
    isSyncToLabModalOpen: boolean;
}

export class CycleActionButtons extends React.Component<ICycleActionButtonsProps, ICycleActionButtonsState> {
    constructor(props: ICycleActionButtonsProps) {
        super(props);

        this.openPreviewCycleModal = this.openPreviewCycleModal.bind(this);
        this.closePreviewCycleModal = this.closePreviewCycleModal.bind(this);
        this.openResetCycleModal = this.openResetCycleModal.bind(this);
        this.closeResetCycleModal = this.closeResetCycleModal.bind(this);
        this.openOpenCycleModal = this.openOpenCycleModal.bind(this);
        this.closeOpenCycleModal = this.closeOpenCycleModal.bind(this);
        this.openCloseCycleModal = this.openCloseCycleModal.bind(this);
        this.closeCloseCycleModal = this.closeCloseCycleModal.bind(this);
        this.openSyncToLabModal = this.openSyncToLabModal.bind(this);
        this.closeSyncToLabModal = this.closeSyncToLabModal.bind(this);
        this.handlePreviewCycle = this.handlePreviewCycle.bind(this);
        this.handleResetCycle = this.handleResetCycle.bind(this);
        this.handleApproveCloseCycle = this.handleApproveCloseCycle.bind(this);
        this.handleOpenCycle = this.handleOpenCycle.bind(this);
        this.handleSyncToLab = this.handleSyncToLab.bind(this);

        this.state = {
            isPreviewCycleModalOpen: false,
            isResetCycleModalOpen: false,
            isOpenCycleModalOpen: false,
            isCloseCycleModalOpen: false,
            isSyncToLabModalOpen: false
        };
    }

    render(): React.ReactNode {
        const { configuration } = this.props;
        if (!configuration) {
            return null;
        }

        return (
            <div className="cycle-action-buttons">
                {this.renderOpenCycleToolTip()}
                {this.renderPreviewCycleButton()}
                {this.renderResetCycleButton()}
                {this.renderCloseCycleButton()}
                {this.renderOpenCycleButton()}
                {this.renderSyncToLabButton()}
            </div>
        );
    }

    private renderOpenCycleToolTip(): React.ReactNode {
        if (this.isAnyButtonVisible() &&
            this.isCurrentButtonDisabled() &&
            !cycleRules.isSelectedCycleClosed) {
            return (
                <Tooltip
                    hoverText={this.getOpenButtonDisabledText()}
                    tooltipText=""
                    display="icon"
                    className="cycle-action-buttons__header-tooltip"
                    id="cycle-action-buttons-tooltip"
                />
            );
        }
        return null;
    }

    private getOpenButtonDisabledText(): string {
        const { configurationsStore } = this.props;
        if (!configurationsStore.convertedCycleSettingsViewCycle.cycleBudgets.length &&
            !workflowSettingsViewStore.assignUserHrApprover.selectedUserId) {
            return 'This cycle is missing a budget and HR administrator.';
        } else if (!configurationsStore.convertedCycleSettingsViewCycle.cycleBudgets.length) {
            return 'This cycle is missing a budget. Create a budget to open the cycle.';
        } else if (!workflowSettingsViewStore.assignUserHrApprover.selectedUserId) {
            return 'This cycle is missing an HR Administrator. Assign a user in your Workflow Settings.';
        } else {
            return '';
        }
    }

    private renderPreviewCycleButton(): React.ReactNode {
        if (!this.isPreviewCycleButtonVisible()) {
            return null;
        }

        return (
            <div className="cycle-action-buttons__button">
                <button
                    disabled={this.isPreviewCycleButtonDisabled()}
                    onClick={this.openPreviewCycleModal}
                    className="pxl-btn pxl-btn--primary cycle-action-buttons__preview-cycle"
                >
                    Preview
                </button>
                <PreviewCycleConfirmationModal
                    closeModal={this.closePreviewCycleModal}
                    isModalOpen={this.state.isPreviewCycleModalOpen}
                    onPreviewCycleClick={this.handlePreviewCycle}
                />
            </div>
        );
    }

    private renderResetCycleButton(): React.ReactNode {
        if (!cycleRules.isCycleInPreviewModeForId(this.props.configuration.cycleId)) {
            return null;
        }

        return (
            <div className="cycle-action-buttons__button">
                <button
                    disabled={this.props.isDisabled}
                    onClick={this.openResetCycleModal}
                    className="pxl-btn pxl-btn--primary cycle-action-buttons__reset-cycle"
                >
                    Reset Cycle
                </button>
                <ResetCycleConfirmationModal
                    closeModal={this.closeResetCycleModal}
                    isModalOpen={this.state.isResetCycleModalOpen}
                    onResetCycleClick={this.handleResetCycle}
                />
            </div>
        );
    }

    private renderOpenCycleButton(): React.ReactNode {
        if (!this.isOpenCycleButtonVisible()) {
            return null;
        }

        return (
            <div className="cycle-action-buttons__button">
                <button
                    disabled={this.isOpenCycleButtonDisabled()}
                    onClick={this.openOpenCycleModal}
                    className="pxl-btn pxl-btn--primary cycle-action-buttons__open-cycle"
                >
                    Open Cycle
                </button>
                <OpenCycleConfirmationModal
                    closeModal={this.closeOpenCycleModal}
                    isModalOpen={this.state.isOpenCycleModalOpen}
                    onNotifyManagersClick={this.handleOpenCycle}
                />
            </div>
        );
    }

    private renderCloseCycleButton(): React.ReactNode {
        if (!this.isCloseCycleButtonVisible()) {
            return null;
        }

        return (
            <div className="cycle-action-buttons__button">
                <button
                    disabled={this.isCloseCycleButtonDisabled()}
                    onClick={this.openCloseCycleModal}
                    className="pxl-btn pxl-btn--primary cycle-action-buttons__close-cycle"
                >
                    Close Cycle
                </button>
                <CloseCycleConfirmationModal
                    closeModal={this.closeCloseCycleModal}
                    isModalOpen={this.state.isCloseCycleModalOpen}
                    onCloseCycleClick={this.handleApproveCloseCycle}
                />
            </div>
        );
    }

    private renderSyncToLabButton(): React.ReactNode {
        // Sync to lab button is visible when the close cycle button is visible
        if (!this.isCloseCycleButtonVisible()) {
            return null;
        }

        return (
            <div className="cycle-action-buttons__button">
                <button
                    disabled={this.isSyncToLabButtonDisabled()}
                    onClick={this.openSyncToLabModal}
                    className="pxl-btn pxl-btn--primary cycle-action-buttons__sync-to-lab"
                >
                    Sync to Insight Lab
                </button>
                <SyncToLabConfirmationModal
                    closeModal={this.closeSyncToLabModal}
                    isModalOpen={this.state.isSyncToLabModalOpen}
                    onSyncToLabClick={this.handleSyncToLab}
                />
            </div>
        );
    }

    // TODO PSP-24209: move config-based rules to cycleRules class
    private isPreviewCycleButtonVisible(): boolean {
        const { configuration, isHrAdmin } = this.props;
        return configuration.isCurrentlyPending &&
            !cycleRules.anyOpenOrClosingCycles &&
            isHrAdmin;
    }

    private isOpenCycleButtonVisible(): boolean {
        const { configuration, isHrAdmin } = this.props;
        return cycleRules.isCyclePendingOrPreview(configuration.cycleId) && isHrAdmin;
    }

    private isCloseCycleButtonVisible(): boolean {
        const { configuration, isHrAdmin } = this.props;
        return !configuration.isCurrentlyPending &&
            !configuration.previewMode &&
            isHrAdmin;
    }

    private isAnyButtonVisible(): boolean {
        return this.isPreviewCycleButtonVisible() ||
            this.isOpenCycleButtonVisible() ||
            this.isCloseCycleButtonVisible();
    }

    private isPreviewCycleButtonDisabled(): boolean {
        const { configuration, isDisabled } = this.props;
        return !cycleRules.isCyclePreviewable(configuration.cycleId) || isDisabled;
    }

    private isOpenCycleButtonDisabled(): boolean {
        const { configuration, isDisabled } = this.props;
        return !configuration.isOpen || !configuration.previewMode ||
            !workflowSettingsViewStore.assignUserHrApprover.selectedUserId || isDisabled;
    }

    private isCloseCycleButtonDisabled(): boolean {
        const { configuration, isDisabled } = this.props;
        return configuration.isClosing ||
            configuration.isClosed ||
            configuration.isPublished ||
            !workflowSettingsViewStore.assignUserHrApprover.selectedUserId ||
            isDisabled;
    }

    private isCurrentButtonDisabled(): boolean {
        if (this.isPreviewCycleButtonVisible()) {
            return this.isPreviewCycleButtonDisabled();
        }
        if (this.isOpenCycleButtonVisible()) {
            return this.isOpenCycleButtonDisabled();
        }
        if (this.isCloseCycleButtonVisible()) {
            return this.isCloseCycleButtonDisabled();
        }
        return false;
    }

    private isSyncToLabButtonDisabled(): boolean {
        const { configuration } = this.props;
        return !configuration.isClosed;
    }

    private openPreviewCycleModal(): void {
        this.setState({ isPreviewCycleModalOpen: true });
    }

    private closePreviewCycleModal(): void {
        this.setState({ isPreviewCycleModalOpen: false });
    }

    private openResetCycleModal(): void {
        this.setState({ isResetCycleModalOpen: true });
    }

    private closeResetCycleModal(): void {
        this.setState({ isResetCycleModalOpen: false });
    }

    private openOpenCycleModal(): void {
        this.setState({ isOpenCycleModalOpen: true });
    }

    private closeOpenCycleModal(): void {
        this.setState({ isOpenCycleModalOpen: false });
    }

    private openCloseCycleModal(): void {
        this.setState({ isCloseCycleModalOpen: true });
    }

    private closeCloseCycleModal(): void {
        this.setState({ isCloseCycleModalOpen: false });
    }

    private openSyncToLabModal(): void {
        this.setState({ isSyncToLabModalOpen: true });
    }

    private closeSyncToLabModal(): void {
        this.setState({ isSyncToLabModalOpen: false });
    }

    private async handlePreviewCycle(): Promise<void> {
        return actions.previewCycle();
    }

    private async handleResetCycle(): Promise<void> {
        const { configurationsStore } = this.props;
        return configurationsStore.resetCycleConfiguration();
    }

    private async handleOpenCycle(): Promise<void> {
        return actions.openCycle();
    }

    private async handleApproveCloseCycle(): Promise<void> {
        return actions.closeCycle();
    }

    private async handleSyncToLab(): Promise<void> {
        return actions.publishCycle();
    }
}

export default observer(CycleActionButtons);